import { spellCount } from "@/lib/spell-count";

/**
 * Whether the heating sheet the resident is about to hand in says anything.
 *
 * Beta Test House 4, 16 September 2026, 20:25: fourteen circuits, five pilot
 * rooms, every circuit left on "Not connected", and the summary let the step
 * finish with a grey "Left unmapped: 1 to 14" under a button that said "See
 * what's next". The house promoted itself to Ready for handoff with no heating
 * in any room. A step whose purpose is the mapping must not finish empty.
 *
 * Deliberate answers still pass: every thermostat marked "a room we're not
 * tracking" is a resident telling us none of these rooms are ours, and a house
 * with no pilot rooms has nothing to map. Only "nothing picked at all, with
 * rooms waiting" is the stop.
 */
export type MappingOutcome = "mapped" | "empty";

export function mappingOutcome(counts: {
  /** Thermostats given a record room. */
  assigned: number;
  /** Circuits still on "Not connected": neither a room nor "not tracking". */
  unassigned: number;
  /** Rooms on the household record that the sheet could name. */
  pilotRooms: number;
}): MappingOutcome {
  if (counts.assigned > 0 || counts.pilotRooms === 0 || counts.unassigned === 0) {
    return "mapped";
  }
  return "empty";
}

/**
 * What the support report reads when the sheet was handed in with no room on
 * it. Honest about a mixed sheet: some circuits marked "not tracking" and the
 * rest unanswered is not "all unmapped".
 */
export const EMPTY_MAPPING_DETAIL = (
  unanswered: number,
  circuits: number,
  rooms: number,
): string =>
  unanswered === circuits
    ? `All ${circuits} heating circuits were left on Not connected. The house has ${rooms} rooms to map.`
    : `${unanswered} of ${circuits} heating circuits were left on Not connected, none was given a room. The house has ${rooms} rooms to map.`;

/**
 * Whether the circuit sheet may be handed in at all.
 *
 * An untouched sheet has no way past it. The mapping is the step: SmartClimate
 * cannot control a floor it cannot name, and the button that used to sit there
 * saved nothing while saying "Save". Beta Test House 13 pressed it with twelve
 * circuits untouched and skipped the step two screens later.
 *
 * "Every circuit is one we do not track" is not an untouched sheet. It is a
 * resident telling us none of these rooms are ours, which is an answer, and it
 * continues like any other. `mappingOutcome` draws that line.
 */
export function sheetMayContinue(outcome: MappingOutcome): boolean {
  return outcome !== "empty";
}

/** Room names as prose: "Kitchen, Living room and Home office". */
function joinRooms(rooms: string[]): string {
  if (rooms.length <= 1) {
    return rooms.join("");
  }
  return `${rooms.slice(0, -1).join(", ")} and ${rooms[rooms.length - 1]}`;
}

/**
 * What the sheet still owes, named room by room.
 *
 * Twelve numbered circuits is an abstract list, and a resident who cannot
 * place the first one has no idea how much of this is really being asked of
 * them. The record knows: four rooms, by name. Saying so turns the sheet into
 * a short errand with an end, and it shrinks as they go.
 *
 * Empty string when the record has no rooms, which is a house with nothing to
 * map rather than a house that is finished.
 */
export function roomsToMapLine(rooms: string[], mapped: string[]): string {
  if (rooms.length === 0) {
    return "";
  }
  const placed = new Set(mapped);
  const left = rooms.filter((room) => !placed.has(room));
  if (left.length === 0) {
    return "Every room has a circuit.";
  }
  const one = left.length === 1;
  const subject = `${spellCount(left.length)} ${one ? "room" : "rooms"}`;
  const verb = one ? "needs" : "need";
  return left.length === rooms.length
    ? `${subject} ${verb} a circuit: ${joinRooms(left)}.`
    : `${subject} still ${verb} a circuit: ${joinRooms(left)}.`;
}
